import { Role } from '@tp-vision/roles-permissions';
import { useMemo } from 'react';
import { useAuth } from '~auth/useAuth';
import {
  canViewReceivedAccessRequests,
  getEffectiveStatus,
  isInternalOrganization,
} from '~components/organization/AccessRequests/accessRequestUtils';
import {
  ServiceAdminOrgAccessRequestFilter,
  ServiceAdminOrgAccessRequestStatus,
} from '~graphql/__generated__/types';
import { useAlertsMenuAccessRequestsQuery } from './__generated__/AlertsMenu.graphql';

export const ACCESS_REQUESTS_POLL_INTERVAL = 15000;

/**
 * Awaiting access requests to the current organization, for its Owner. They are not stored as
 * alerts: once a request is accepted or expires it drops out.
 *
 * Only one caller should poll (the Alerts menu); others read the same query from the cache.
 */
export function useAwaitingAccessRequests({ poll }: { poll: boolean }) {
  const auth = useAuth();

  const canView =
    (auth.user.roles?.includes(Role.Owner) ?? false) &&
    canViewReceivedAccessRequests(
      auth.verifyUserPermissions,
      isInternalOrganization(auth.organization?.id),
    );

  const query = useAlertsMenuAccessRequestsQuery({
    variables: { filter: ServiceAdminOrgAccessRequestFilter.Awaiting },
    pollInterval: poll ? ACCESS_REQUESTS_POLL_INTERVAL : undefined,
    fetchPolicy: poll ? 'network-only' : 'cache-and-network',
    nextFetchPolicy: poll ? undefined : 'cache-first',
    skip: !canView,
  });

  const awaitingAccessRequests = useMemo(
    () =>
      (query.data?.organizationAccessRequests ?? []).filter(
        (request) => getEffectiveStatus(request) === ServiceAdminOrgAccessRequestStatus.Awaiting,
      ),
    [query.data],
  );

  return { canView, query, awaitingAccessRequests };
}

/** "1d 4h" — access is requested in days and hours only, so minutes are left out. */
export function formatRequestedDuration(totalMinutes: number): string {
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);

  return [days ? `${days}d` : null, hours || !days ? `${hours}h` : null].filter(Boolean).join(' ');
}

export function getRequesterName(
  requester?: {
    givenName?: string | null;
    familyName?: string | null;
    email: string;
  } | null,
) {
  return (
    [requester?.givenName, requester?.familyName].filter(Boolean).join(' ') ||
    requester?.email ||
    '-'
  );
}
